import { VirtualRoom } from './services/VirtualRoomStorage';

export interface RoomBorderRendererOptions {
  tileSize: number;
  borderColor: string;
  borderWidth: number;
  borderOpacity: number;
  depth: number;
}

export class RoomBorderRenderer {
  private scene: Phaser.Scene | null = null;
  private graphics: Map<string, Phaser.GameObjects.Graphics> = new Map(); // Track graphics by room ID
  private options: RoomBorderRendererOptions;

  constructor(options: Partial<RoomBorderRendererOptions> = {}) {
    this.options = {
      tileSize: 32,
      borderColor: '#ff0000',
      borderWidth: 2,
      borderOpacity: 0.8,
      depth: 1000,
      ...options
    };
  }

  /**
   * Set the Phaser scene for rendering
   */
  public setScene(scene: Phaser.Scene): void {
    this.scene = scene;
  }

  /**
   * Update the renderer options
   */
  public updateOptions(options: Partial<RoomBorderRendererOptions>): void {
    this.options = { ...this.options, ...options };
  }

  /**
   * Clear all existing borders
   */
  public clearBorders(): void {
    this.graphics.forEach((graphics, roomId) => {
      if (graphics && graphics.destroy) {
        graphics.destroy();
      }
    });
    this.graphics.clear();
  }

  /**
   * Remove border graphics for a specific room
   */
  public clearRoomBorder(roomId: string): void {
    const graphics = this.graphics.get(roomId);
    if (graphics && graphics.destroy) {
      graphics.destroy();
    }
    this.graphics.delete(roomId);
  }

  /**
   * Paint a continuous border around the entire room
   */
  public paintRoomBorder(tiles: Array<{ x: number; y: number }>, room: VirtualRoom): void {
    if (!this.scene) {
      console.warn('[RoomBorderRenderer] Scene not available for border painting');
      return;
    }

    // Remove existing border for this room if it exists
    const existingGraphics = this.graphics.get(room.id);
    if (existingGraphics && existingGraphics.destroy) {
      existingGraphics.destroy();
      this.graphics.delete(room.id);
    }

    if (tiles.length === 0) {
      console.log('[RoomBorderRenderer] No tiles to paint borders for room:', room.name);
      return;
    }

    // Convert room color to number
    const borderColor = parseInt(room.color.replace('#', ''), 16);
    
    // Create a single graphics object for this room's border
    const borderGraphics = this.scene.add.graphics();
    borderGraphics.setDepth(this.options.depth);
    borderGraphics.lineStyle(this.options.borderWidth, borderColor, this.options.borderOpacity);
    
    // Create a set of all room tiles for quick lookup
    const tileSet = new Set(tiles.map(t => `${t.x},${t.y}`));
    
    // Find all edges that need borders (edges between room tiles and empty space)
    const edges: Array<{ x1: number; y1: number; x2: number; y2: number }> = [];
    
    for (const tile of tiles) {
      const { x, y } = tile;
      
      // Check each side of the tile explicitly
      // Top edge: check if tile above is empty
      if (!tileSet.has(`${x},${y - 1}`)) {
        edges.push({ x1: x, y1: y, x2: x + 1, y2: y });
      }
      
      // Bottom edge: check if tile below is empty
      if (!tileSet.has(`${x},${y + 1}`)) {
        edges.push({ x1: x, y1: y + 1, x2: x + 1, y2: y + 1 });
      }
      
      // Left edge: check if tile to the left is empty
      if (!tileSet.has(`${x - 1},${y}`)) {
        edges.push({ x1: x, y1: y, x2: x, y2: y + 1 });
      }
      
      // Right edge: check if tile to the right is empty
      if (!tileSet.has(`${x + 1},${y}`)) {
        edges.push({ x1: x + 1, y1: y, x2: x + 1, y2: y + 1 });
      }
    }
    
    // Draw all outer edges
    for (const edge of edges) {
      const pixelX1 = edge.x1 * this.options.tileSize;
      const pixelY1 = edge.y1 * this.options.tileSize;
      const pixelX2 = edge.x2 * this.options.tileSize;
      const pixelY2 = edge.y2 * this.options.tileSize;
      
      borderGraphics.beginPath();
      borderGraphics.moveTo(pixelX1, pixelY1);
      borderGraphics.lineTo(pixelX2, pixelY2);
      borderGraphics.strokePath();
    }
    
    // Store the graphics reference for this room
    this.graphics.set(room.id, borderGraphics);
    
    console.log('[RoomBorderRenderer] Painted continuous border with', edges.length, 'edges for room:', room.name);
  }

  /**
   * Get the current graphics object
   */
  public getGraphics(): Phaser.GameObjects.Graphics | null {
    // Return the first graphics object if any exist
    const firstGraphics = this.graphics.values().next().value;
    return firstGraphics || null;
  }

  /**
   * Check if borders are currently rendered
   */
  public hasBorders(): boolean {
    return this.graphics.size > 0;
  }

  /**
   * Destroy the renderer and clean up resources
   */
  public destroy(): void {
    this.clearBorders();
    this.scene = null;
  }
} 